import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Loader2, Building2, Save, Sparkles, ShieldCheck, AlertCircle, X,
} from 'lucide-react';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];
const EMPTY_FORM = {
  company_name: '', about: '', industry: '', company_size: '', founded_year: '',
  website_url: '', linkedin_url: '', instagram_url: '',
  contact_email: '', contact_phone: '',
  logo_url: '', banner_url: '',
  primary_venue_id: '',
  category_ids: [],
  expertise_tags: [],
  services_summary: '',
};

const MyEcosystemProfilePage = () => {
  const navigate = useNavigate();
  const { api, user, isAuthenticated, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?redirect=/ecosystem/my-profile');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [c, v, p] = await Promise.all([
          api.get('/ecosystem/categories'),
          api.get('/public/venues'),
          api.get('/ecosystem/my-profile').catch(() => ({ data: null })),
        ]);
        setCategories(c.data || []);
        setVenues(v.data || []);
        if (p.data) {
          setProfile(p.data);
          setForm({
            ...EMPTY_FORM,
            ...p.data,
            category_ids: (p.data.categories || []).map((x) => x.id),
            expertise_tags: p.data.expertise_tags || [],
            founded_year: p.data.founded_year || '',
            company_size: p.data.company_size || '',
            logo_url: p.data.logo_url || '',
            banner_url: p.data.banner_url || '',
            services_summary: p.data.services_summary || '',
            website_url: p.data.website_url || '',
            linkedin_url: p.data.linkedin_url || '',
            instagram_url: p.data.instagram_url || '',
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [api, isAuthenticated]);

  const toggleCategory = (id) => {
    setForm((f) => ({
      ...f,
      category_ids: f.category_ids.includes(id)
        ? f.category_ids.filter((x) => x !== id)
        : f.category_ids.length >= 8
          ? (toast.error('Max 8 categories'), f.category_ids)
          : [...f.category_ids, id],
    }));
  };

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (!t) return;
    if (form.expertise_tags.length >= 15) {
      toast.error('Max 15 tags');
      return;
    }
    if (!form.expertise_tags.includes(t)) {
      setForm({ ...form, expertise_tags: [...form.expertise_tags, t] });
    }
    setTagDraft('');
  };

  const removeTag = (t) => setForm({ ...form, expertise_tags: form.expertise_tags.filter((x) => x !== t) });

  const buildPayload = () => {
    const p = { ...form };
    if (!p.founded_year) delete p.founded_year;
    else p.founded_year = parseInt(p.founded_year);
    ['company_size', 'website_url', 'linkedin_url', 'instagram_url', 'logo_url', 'banner_url', 'services_summary']
      .forEach((k) => { if (!p[k]) delete p[k]; });
    return p;
  };

  const save = async () => {
    if (form.category_ids.length === 0) {
      toast.error('Select at least one category');
      return;
    }
    if (!profile && (!form.primary_venue_id)) {
      toast.error('Select your primary Clayworks centre');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (profile) {
        // PATCH (excluding immutable fields)
        delete payload.company_name;
        delete payload.primary_venue_id;
        const r = await api.patch('/ecosystem/my-profile', payload);
        setProfile(r.data);
        toast.success(r.data.status === 'pending' ? 'Saved! Sensitive changes are pending re-approval.' : 'Profile updated');
      } else {
        const r = await api.post('/ecosystem/my-profile', payload);
        setProfile(r.data);
        toast.success('Profile submitted! Our team will review and approve shortly.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#c8a97e]" /></div>;
  }

  const statusBadge = () => {
    if (!profile) return null;
    const map = {
      approved: { label: 'Approved · Live', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      pending: { label: 'Pending Approval', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
      suspended: { label: 'Suspended', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
    };
    const s = map[profile.status] || map.pending;
    return <Badge className={`${s.cls} border`}>{s.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#faf6ec] py-8" data-testid="my-eco-profile">
      <div className="max-w-4xl mx-auto px-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#5c5750] hover:text-[#2d2a26] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#c8a97e] font-bold mb-1">
              <Sparkles className="w-3.5 h-3.5" /> CLAYWORKS ECOSYSTEM
            </div>
            <h1 className="text-3xl font-heading font-bold text-[#2d2a26]">
              {profile ? 'Manage your company profile' : 'List your company'}
            </h1>
            <p className="text-sm text-[#7a7268] mt-1">
              {profile
                ? 'Edits to sensitive fields will trigger a fresh admin review.'
                : 'Tell the Clayworks community about your business. Profiles are reviewed by admin before going live.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge()}
            {profile?.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
          </div>
        </div>

        {profile?.status === 'pending' && (
          <Card className="border-amber-200 bg-amber-50 mb-5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                Your profile is queued for admin review. Once approved it'll appear in the public directory.
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-[#e5dfd0]">
          <CardContent className="p-6 space-y-6">
            <section className="space-y-4">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">BASICS</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Company name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    disabled={!!profile}
                    data-testid="form-company-name"
                  />
                  {profile && <p className="text-xs text-muted-foreground mt-1">Name is locked once submitted. Contact admin to change.</p>}
                </div>
                <div>
                  <Label>Industry *</Label>
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. SaaS, Fintech, Boutique law firm" data-testid="form-industry" />
                </div>
                <div>
                  <Label>Company size</Label>
                  <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Founded year</Label>
                  <Input type="number" value={form.founded_year} onChange={(e) => setForm({ ...form, founded_year: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Primary Clayworks centre *</Label>
                  <Select
                    value={form.primary_venue_id}
                    onValueChange={(v) => setForm({ ...form, primary_venue_id: v })}
                    disabled={!!profile}
                  >
                    <SelectTrigger data-testid="form-venue"><SelectValue placeholder="Pick the centre you operate from" /></SelectTrigger>
                    <SelectContent>
                      {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">ABOUT (PUBLIC)</h3>
              <div>
                <Label>About — 20 to 2000 chars *</Label>
                <Textarea rows={5} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} data-testid="form-about" />
                <p className="text-xs text-muted-foreground">{form.about.length}/2000</p>
              </div>
              <div>
                <Label>Services summary (one-liner)</Label>
                <Textarea rows={2} value={form.services_summary} onChange={(e) => setForm({ ...form, services_summary: e.target.value })} placeholder="What you do, in one breath." />
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">CATEGORIES * (1-8)</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggleCategory(c.id)}
                    data-testid={`cat-${c.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      form.category_ids.includes(c.id)
                        ? 'bg-[#2d2a26] text-[#c8a97e] border-[#2d2a26]'
                        : 'bg-white text-[#5c5750] border-[#e5dfd0] hover:bg-[#faf6ec]'
                    }`}
                  >{c.name}</button>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">EXPERTISE TAGS (max 15)</h3>
              <div className="flex gap-2">
                <Input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)} placeholder="e.g. b2b-saas, gst-filing, brand-design" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} data-testid="tag-input" />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {form.expertise_tags.map((t) => (
                  <Badge key={t} className="bg-[#faf6ec] text-[#5c5750] border border-[#e5dfd0] gap-1">
                    #{t}
                    <button onClick={() => removeTag(t)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">CONTACT</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Contact email *</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="form-email" />
                </div>
                <div>
                  <Label>Contact phone *</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="form-phone" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">These are kept internal — inquiries route through your Centre Manager.</p>
            </section>

            <section className="space-y-3">
              <h3 className="text-xs font-bold tracking-[0.2em] text-[#7a7268]">BRAND</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." /></div>
                <div><Label>Banner URL</Label><Input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} placeholder="https://..." /></div>
                <div><Label>Website</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://..." /></div>
                <div><Label>LinkedIn</Label><Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/company/..." /></div>
                <div><Label>Instagram</Label><Input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} placeholder="https://instagram.com/..." /></div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#f0e7d4]">
              <Button variant="outline" onClick={() => navigate('/ecosystem')}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#2d2a26] hover:bg-[#1d1a16] text-[#c8a97e]" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {profile ? 'Save changes' : 'Submit for review'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyEcosystemProfilePage;
